/**
 * Queue tab — pure helpers: humanized test duration, status badge tone,
 * and layer ⇄ element-kind / label mapping.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowLayer, QueueElementState } from '@trafficpulse/shared';

export type QueueBadgeTone = 'warning' | 'success' | 'secondary';

interface QueueStatusMeta {
  label: string;
  tone: QueueBadgeTone;
}

const QUEUE_STATUS: Record<QueueElementState, QueueStatusMeta> = {
  // Added but not yet marked ready — never promoted until made ready.
  waiting: { label: 'Waiting', tone: 'secondary' },
  queued: { label: 'Queued', tone: 'warning' },
  testing: { label: 'Live', tone: 'success' },
  // Wire state stays "removed"; the product name is Eliminated.
  removed: { label: 'Eliminated', tone: 'secondary' },
};

export function queueStatusMeta(state: QueueElementState): QueueStatusMeta {
  return QUEUE_STATUS[state];
}

/** Compact duration ("45s", "12m", "3h 5m", "2d 4h"); "—" for null/invalid. */
export function humanizeDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.floor(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/** Elapsed time from an ISO test-start to now, humanized; "—" if unset. */
export function testedForLabel(testStartedAt: string | null): string {
  if (!testStartedAt) return '—';
  const started = new Date(testStartedAt).getTime();
  if (Number.isNaN(started)) return '—';
  return humanizeDuration((Date.now() - started) / 1000);
}

export function layerToElementKind(layer: FlowLayer): 'path' | 'lander' | 'offer' {
  if (layer === 'paths') return 'path';
  return layer === 'landers' ? 'lander' : 'offer';
}

export function layerLabel(layer: FlowLayer): string {
  if (layer === 'paths') return 'Path';
  return layer === 'landers' ? 'Lander' : 'Offer';
}

/** Stable key for pairing a queue element with its simulated-stats row. */
export function simStatsKey(layer: FlowLayer, bankId: string): string {
  return `${layerToElementKind(layer)}:${bankId}`;
}
